import React, { useState, useEffect } from "react";
import { Send, X, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { localInputToUTC, calculateAuctionEnd } from "@/lib/timezone";

type Invite = {
  id: string;
  email: string;
  lawFirm: string;
};

type RecentContact = {
  email: string;
  lawFirm: string;
};

export const InviteForm = () => {
  const [email, setEmail] = useState("");
  const [lawFirm, setLawFirm] = useState("");
  const [invites, setInvites] = useState<Invite[]>([]);
  const [recentContacts, setRecentContacts] = useState<RecentContact[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Load saved invites and recent contacts from localStorage on component mount
  useEffect(() => {
    const savedInvites = localStorage.getItem("proposal-invites");
    const savedEmail = localStorage.getItem("proposal-invite-email");
    const savedLawFirm = localStorage.getItem("proposal-invite-lawfirm");
    const savedRecentContacts = localStorage.getItem("recent-invite-contacts");
    
    if (savedInvites) {
      try {
        setInvites(JSON.parse(savedInvites));
      } catch (error) {
        console.error("Error parsing saved invites:", error);
      }
    }
    
    if (savedRecentContacts) {
      try {
        setRecentContacts(JSON.parse(savedRecentContacts));
      } catch (error) {
        console.error("Error parsing recent contacts:", error);
      }
    }
    
    if (savedEmail) setEmail(savedEmail);
    if (savedLawFirm) setLawFirm(savedLawFirm);
  }, []);
  
  // Save invites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("proposal-invites", JSON.stringify(invites));
  }, [invites]);
  
  // Save form inputs to localStorage when they change
  useEffect(() => {
    localStorage.setItem("proposal-invite-email", email);
  }, [email]);
  
  useEffect(() => {
    localStorage.setItem("proposal-invite-lawfirm", lawFirm);
  }, [lawFirm]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    // Basic validation for invites
    if (invites.length === 0) {
      toast({
        title: "No Invites",
        description: "Please add at least one invite before sending.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/signin");
        return;
      }

      // Load all proposal data from localStorage
      const summaryData = localStorage.getItem("proposal-summary");
      const reverseAuctionData = localStorage.getItem("proposal-reverse-auction");
      const questionsData = localStorage.getItem("proposal-questions");
      const pricingData = localStorage.getItem("proposal-pricing");
      
      if (!summaryData) {
        toast({
          title: "Missing Data",
          description: "The proposal summary is required. Please go back and complete it.",
          variant: "destructive"
        });
        setIsSubmitting(false);
        return;
      }

      const summary = JSON.parse(summaryData);
      const reverseAuction = reverseAuctionData ? JSON.parse(reverseAuctionData) : null;
      const questions = questionsData ? JSON.parse(questionsData) : null;
      const pricing = pricingData ? JSON.parse(pricingData) : null;

      // Properly convert dates to UTC using the timezone utility
      // The user inputs times in Europe/London timezone
      let submissionDeadlineUtc: string;
      let auctionStartUtc: string;
      let auctionEndUtc: string;

      if (reverseAuction?.proposalDueDate && reverseAuction?.proposalDueTime) {
        // Convert London time input to UTC
        submissionDeadlineUtc = localInputToUTC(
          new Date(reverseAuction.proposalDueDate),
          reverseAuction.proposalDueTime
        );
      } else {
        // Default: 7 days from now
        submissionDeadlineUtc = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      }

      if (reverseAuction?.auctionStartDate && reverseAuction?.auctionStartTime) {
        // Convert London time input to UTC
        auctionStartUtc = localInputToUTC(
          new Date(reverseAuction.auctionStartDate),
          reverseAuction.auctionStartTime
        );
      } else {
        // Default: 8 days from now
        auctionStartUtc = new Date(Date.now() + 8 * 24 * 60 * 60 * 1000).toISOString();
      }

      // Calculate auction end based on duration
      if (reverseAuction?.auctionDuration) {
        auctionEndUtc = calculateAuctionEnd(
          auctionStartUtc,
          reverseAuction.auctionDuration,
          reverseAuction.customWeeks,
          reverseAuction.customDays,
          reverseAuction.customHours
        );
      } else {
        // Default: 24 hours after auction start
        auctionEndUtc = new Date(new Date(auctionStartUtc).getTime() + 24 * 60 * 60 * 1000).toISOString();
      }

      // Create RFP in database with properly converted UTC timestamps
      const { data: rfp, error: rfpError } = await supabase
        .from("rfps")
        .insert({
          buyer_id: user.id,
          title: summary.title || "RFP",
          description: summary.description || "",
          category: summary.category || null,
          budget_range: summary.budgetRange || null,
          region: summary.region || null,
          team: summary.team || null,
          matter_name: summary.matterName || null,
          submission_deadline: submissionDeadlineUtc,
          auction_start: auctionStartUtc,
          auction_end: auctionEndUtc,
          status: "OPEN_FOR_SUBMISSIONS",
          questions_data: questions,
          pricing_data: pricing,
        })
        .select()
        .single();

      if (rfpError) throw rfpError;

      // Create supplier invitations
      const invitations = invites.map(invite => ({
        rfp_id: rfp.id,
        supplier_name: invite.lawFirm,
        supplier_email: invite.email,
        invite_token: crypto.randomUUID(),
      }));

      const { data: createdInvitations, error: invitationsError } = await supabase
        .from("supplier_invitations")
        .insert(invitations)
        .select();

      if (invitationsError) throw invitationsError;

      // Get buyer profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("name")
        .eq("id", user.id)
        .single();

      // Send invitation emails
      const { error: emailError } = await supabase.functions.invoke("send-rfp-invitations", {
        body: {
          rfpId: rfp.id,
          invitations: createdInvitations.map(inv => ({
            id: inv.id,
            supplierName: inv.supplier_name,
            supplierEmail: inv.supplier_email,
            inviteToken: inv.invite_token,
          })),
          rfpTitle: rfp.title,
          rfpDescription: rfp.description,
          submissionDeadline: rfp.submission_deadline,
          auctionStart: rfp.auction_start,
          auctionEnd: rfp.auction_end,
          buyerName: profile?.name || "A buyer",
        },
      });

      if (emailError) {
        console.error("Email error:", emailError);
        toast({
          title: "Warning",
          description: "RFP created but some invitation emails may not have been sent",
        });
      } else {
        toast({
          title: "Success",
          description: `RFP created and ${invites.length} invitations sent!`,
        });
      }
      
      // Clear all saved data after successful submission
      localStorage.removeItem("proposal-summary");
      localStorage.removeItem("proposal-pricing");
      localStorage.removeItem("proposal-questions");
      localStorage.removeItem("proposal-reverse-auction");
      localStorage.removeItem("proposal-invites");
      localStorage.removeItem("proposal-invite-email");
      localStorage.removeItem("proposal-invite-lawfirm");
      localStorage.removeItem("proposal-active-section");
      
      // Navigate to auctions page
      navigate("/auctions");
    } catch (error: any) {
      console.error("Error creating RFP:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create RFP. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddInvite = () => {
    if (!email.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter an email address to add an invite.",
        variant: "destructive"
      });
      return;
    }

    if (!lawFirm.trim()) {
      toast({
        title: "Law Firm Required",
        description: "Please enter a law firm name.",
        variant: "destructive"
      });
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    const newInvite: Invite = {
      id: Date.now().toString(),
      email: email.trim(),
      lawFirm: lawFirm.trim()
    };

    setInvites([...invites, newInvite]);
    
    // Save to recent contacts (avoid duplicates, keep max 10)
    const newContact: RecentContact = { email: email.trim(), lawFirm: lawFirm.trim() };
    const updatedRecent = [
      newContact,
      ...recentContacts.filter(c => c.email.toLowerCase() !== newContact.email.toLowerCase())
    ].slice(0, 10);
    setRecentContacts(updatedRecent);
    localStorage.setItem("recent-invite-contacts", JSON.stringify(updatedRecent));
    
    // Clear the form inputs
    setEmail("");
    setLawFirm("");
    
    // Clear the localStorage for the inputs
    localStorage.removeItem("proposal-invite-email");
    localStorage.removeItem("proposal-invite-lawfirm");

    toast({
      title: "Invite Added",
      description: `Added ${newInvite.email} to the invite list.`,
    });
  };

  const handleSelectRecentContact = (contact: RecentContact) => {
    // Check if already in invites list
    if (invites.some(inv => inv.email.toLowerCase() === contact.email.toLowerCase())) {
      toast({
        title: "Already Added",
        description: `${contact.email} is already in the invite list.`,
        variant: "destructive"
      });
      return;
    }
    
    const newInvite: Invite = {
      id: Date.now().toString(),
      email: contact.email,
      lawFirm: contact.lawFirm
    };
    
    setInvites([...invites, newInvite]);
    
    toast({
      title: "Invite Added",
      description: `Added ${contact.email} to the invite list.`,
    });
  };

  const handleRemoveInvite = (id: string) => {
    const removedInvite = invites.find(invite => invite.id === id);
    setInvites(invites.filter(invite => invite.id !== id));
    
    if (removedInvite) {
      toast({
        title: "Invite Removed",
        description: `Removed ${removedInvite.email} from the invite list.`,
      });
    }
  };

  return (
    <div className="transition-opacity">
      <div className="flex items-center mb-4">
        <Send className="h-6 w-6 text-black mr-2" />
        <h2 className="text-xl font-semibold text-gray-800">Invite</h2>
      </div>
      <p className="text-gray-600 mb-6">Invite clients and collaborators to view the RFP/Instructions</p>
      
      <Card className="mt-4">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="space-y-2 flex-1">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email"
                  type="email" 
                  placeholder="client@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2 flex-1">
                <Label htmlFor="lawFirm">Law Firm</Label>
                <Input 
                  id="lawFirm"
                  type="text" 
                  placeholder="Law Firm Name"
                  value={lawFirm}
                  onChange={(e) => setLawFirm(e.target.value)}
                  className="w-full"
                />
              </div>

              <div className="flex items-end">
                <Button 
                  type="button" 
                  onClick={handleAddInvite}
                  className="h-10 px-4"
                >
                  <Plus size={18} />
                  Add
                </Button>
              </div>
            </div>

            {/* Recent Contacts */}
            {recentContacts.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Recent Contacts</h3>
                <div className="flex flex-wrap gap-2">
                  {recentContacts
                    .filter(contact => !invites.some(inv => inv.email.toLowerCase() === contact.email.toLowerCase()))
                    .map((contact, index) => (
                      <Button
                        key={index}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleSelectRecentContact(contact)}
                        className="text-xs"
                      >
                        <Plus size={14} className="mr-1" />
                        {contact.lawFirm} ({contact.email})
                      </Button>
                    ))}
                </div>
              </div>
            )}
            
            {invites.length > 0 && (
              <div className="mt-6">
                <h3 className="text-md font-medium mb-2">Invites</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {invites.map((invite) => (
                    <div 
                      key={invite.id}
                      className="flex items-center justify-between bg-gray-50 p-3 rounded-md"
                    >
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        {invite.lawFirm && (
                          <p className="text-sm text-gray-600">{invite.lawFirm}</p>
                        )}
                      </div>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleRemoveInvite(invite.id)}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {invites.length > 0 && (
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Send Invites"}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
